import { Paper, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/Auth/AuthContext';
import { EditMessageProvider } from '../../context/EditingMessage/EditingMessageContext';
import { ReplyMessageProvider } from '../../context/ReplyingMessage/ReplyingMessageContext';
import toastError from '../../errors/toastError';
import api from '../../services/api';
import openSocket from '../../services/socket-io';
import ContactDrawer from '../ContactDrawer';
import MessageInput from '../MessageInput/';
import MessagesList from '../MessagesList';
import TicketActionButtons from '../TicketActionButtons';
import TicketHeader from '../TicketHeader';
import TicketInfo from '../TicketInfo';

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  ticketInfo: {
    backgroundColor: theme.palette.background.default,
    maxWidth: '50%',
    flexBasis: '50%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '80%',
      flexBasis: '80%',
    },
  },
  ticketActionButtons: {
    backgroundColor: theme.palette.background.default,
    maxWidth: '50%',
    flexBasis: '50%',
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      flexBasis: '100%',
      marginBottom: '5px',
    },
  },
  mainWrapper: {
    flex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: '0',
    marginRight: -drawerWidth,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [typingIndicator, setTypingIndicator] = useState('');
  const typingTimeoutRef = useRef(null);
  const { user } = useContext(AuthContext);

  const socketRef = useRef(null);

  // Fetch ticket info
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get('/tickets/' + ticketId);
          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  // Socket connection 
  useEffect(() => {

    const socket = openSocket();
    if (!socket) return undefined;
    socketRef.current = socket;

    const joinRoom = () => {
      if (socket.connected) {
        socket.emit('joinChatBox', ticketId);
      }
    };

    const handleTicketUpdate = (data) => {

      if (data.action === 'update') {
        setTicket((prev) => ({ ...prev, ...data.ticket }));
      }

      if (data.action === 'delete') {
        toast.success('Ticket deletado com sucesso.');
        history.push('/tickets');
      }
    };

    const handleContactUpdate = (data) => {

      if (data.action === 'update') {
        setContact((prevState) =>
          prevState.id === data.contact?.id ? { ...prevState, ...data.contact } : prevState
        );
      }
    };

    const handleTicketTyping = (data) => {
      if (!data || Number(data.ticketId) !== Number(ticketId)) {
        return;
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      if (!data.typing) {
        setTypingIndicator('');
        return;
      }

      if (data.actorType === 'bot') {
        setTypingIndicator('Bot esta digitando...');
      } else if (data.actorType === 'attendant') {
        if (Number(data.userId) === Number(user?.id)) {
          return;
        }
        setTypingIndicator('Atendente esta digitando...');
      }

      typingTimeoutRef.current = setTimeout(() => {
        setTypingIndicator('');
      }, 7000);
    };

    // Se já estiver conectado, entra na sala imediatamente
    if (socket.connected) {
      joinRoom();
    } else {
      socket.on('connect', joinRoom);
    }

    // Adicionar listeners
    socket.on('ticket', handleTicketUpdate);
    socket.on('contact', handleContactUpdate);
    socket.on('ticketTyping', handleTicketTyping);

    // Handler para reconexão
    const handleReconnect = () => {
      console.log('♻️ Ticket - Reconectado, reentrando na sala...');
      joinRoom();
    };

    socket.on('reconnect', handleReconnect);


    return () => {

      socket.off('connect', joinRoom);
      socket.off('ticket', handleTicketUpdate);
      socket.off('contact', handleContactUpdate);
      socket.off('ticketTyping', handleTicketTyping);
      socket.off('reconnect', handleReconnect);

      if (socket.connected) {
        socket.emit('leaveChatBox', ticketId);
      }

      socketRef.current = null;
      setTypingIndicator('');
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

    };
  }, [ticketId, history, user?.id]);

  const emitEvent = (event, payload) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, payload);
    } else {
      console.warn('Socket não conectado, evento não emitido:', event);
    }
  };

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, { [classes.mainWrapperShift]: drawerOpen })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
              typingIndicator={typingIndicator}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        <ReplyMessageProvider>
          <EditMessageProvider>
            <MessagesList ticketId={ticketId} isGroup={ticket.isGroup} />
            <MessageInput ticketStatus={ticket.status} emitEvent={emitEvent} />
          </EditMessageProvider>
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
